import { getDetachedDay, getAttachedDate } from "@laborchart-modules/common/dist/datetime";
import { PageTitle, type CustomField } from "@/react/prop-types";
import { processCustomFields } from "@/react/shared/custom-field-utils";
import type { AssignmentFormValues, AssignmentListData, OverTimeRates } from "./assignment/types";
import type { RequestFormValues, RequestListData } from "./request/types";
import type {
   ActivitiesResponseProps,
   BaggageTimeKey,
   CommonPayloadForRequestAndAssignment,
   ProjectOptions,
   ValueType,
} from "./types";
import { timeOptions } from "@/lib/utils/timezones";
import {
   dayNameToNumberMap,
   daysOfWeekAbbreviations,
   numberToDayNameMap,
} from "@/react/shared/constants";
import Resizer from "react-image-file-resizer";
import type { SerializedAttachment } from "@laborchart-modules/common/dist/rethink/serializers/attachment-serializer";
import type { AttachmentsData } from "./attachments/types";
import type { TagData } from "./tags/types";
import type { ActivityCategory } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type { ActivityEntityType } from "@laborchart-modules/common/dist/reql-builder/procedures/find-activity";
import { CurrencyFormatter, formatNumber } from "@procore/globalization-toolkit";
import { translationsLabelsActivityCard } from "./constants";
import { CustomFieldType } from "@laborchart-modules/common/dist/rethink/schemas/common/custom-field-type";
import {
   renderAdd,
   renderChange,
   renderColorAdd,
   renderColorChange,
   renderColorRemove,
   renderRemove,
} from "./activities/activity-helper";

import { addWeeks, isAfter } from "date-fns";
import { convertUtcDateValueToDateWithoutTimezone } from "@/lib/utils/date-2";
import type { Dispatch, SetStateAction } from "react";
import type { useI18nContext } from "@procore/core-react";
import type { AssignmentWorkDays } from "@/models/assignment";
import type { WorkDays } from "@laborchart-modules/common/dist/postgres/schemas/common/custom-json";

export const NEXT_GROUP_ID_PREFIX = "children-of-";

export type CategoryTieredSelectOption = {
   id: string;
   label: string;
   groupId: string | null;
   nextGroupId?: string;
};

/**
 * Builds the full path from root to the selected item by walking up the ancestor chain.
 * Supports arbitrary nesting depth (child -> parent -> grandparent -> ... -> root).
 */
export const buildTieredSelectPath = (
   options: CategoryTieredSelectOption[],
   selectedId: string,
): CategoryTieredSelectOption[] => {
   const optionsById = new Map(options.map((o) => [o.id, o]));
   const path: CategoryTieredSelectOption[] = [];
   let current = optionsById.get(selectedId);

   while (current) {
      path.unshift(current);
      if (current.groupId) {
         const parentId = current.groupId.replace(NEXT_GROUP_ID_PREFIX, "");
         current = optionsById.get(parentId);
      } else {
         break;
      }
   }

   return path;
};

/**
 * Maps a TieredSelect selection to the form's category/subcategory fields.
 * A nested selection becomes the subcategory, and its direct parent becomes the category.
 *
 * @param options - Every category option in the picker, used to look up the selection's parent
 * @param selectedValue - Path from the top-level category down to the selected category
 * @param setFieldValue - Formik setter for the `category` and `subcategory` fields
 */
export const syncFormCategoryFields = (
   options: CategoryTieredSelectOption[],
   selectedValue: CategoryTieredSelectOption[],
   setFieldValue: (field: string, value: any) => void,
) => {
   if (selectedValue.length === 0) {
      setFieldValue("category", null);
      setFieldValue("subcategory", null);
      return;
   }

   const selected = selectedValue[selectedValue.length - 1];

   if (selected.groupId) {
      const parentId = selected.groupId.replace(NEXT_GROUP_ID_PREFIX, "");
      const parent = options.find((option) => option.id === parentId);
      if (parent == null) {
         throw new Error(`Category ${selected.id} has no parent ${parentId} in the options`);
      }

      setFieldValue("category", { id: parent.id, label: parent.label });
      setFieldValue("subcategory", { id: selected.id, label: selected.label });
   } else {
      // Selected item is a top-level category
      setFieldValue("category", { id: selected.id, label: selected.label });
      setFieldValue("subcategory", null);
   }
};

export const getCommonFieldsForRequestAndAssignment = (
   values: AssignmentFormValues | RequestFormValues,
   customFields: CustomField[] | null,
): CommonPayloadForRequestAndAssignment => ({
   project_id: values.project!.id,
   category_id: values.category?.id ?? null,
   subcategory_id: values.subcategory?.id ?? null,
   start_day: getDetachedDay(new Date(values.start_date!)),
   end_day: getDetachedDay(new Date(values.end_date!)),
   start_time: values.assignment_by_time ? values.start_time.id : null,
   end_time: values.assignment_by_time ? values.end_time.id : null,
   percent_allocated: values.assignment_by_allocation ? values.percent_allocated : null,
   status_id: values.status?.id ?? null,
   work_days: values.work_days,
   custom_fields: customFields ? processCustomFields(customFields, values) : {},
   instruction_text: values.instruction_text ?? null,
   work_scope_text: values.work_scope_text ?? null,
   comments: values.comments
      ? [
           {
              author_id: values.author_id ?? "",
              content: values.comments,
           },
        ]
      : undefined,
});

/**
 * Builds the project change for one assignment or request in a bulk edit.
 *
 * A cost code and label belong to a single project,
 * so an assignment or request moving to another project has both cleared.
 *
 * @param currentProjectId - The project the assignment or request is on now
 * @param pickedProjectId - The project picked in the bulk edit form, if any
 * @returns The new project with a cleared cost code and label,
 * or an empty object when the assignment or request stays on its project
 */
export const getProjectMove = (currentProjectId: string, pickedProjectId: string | undefined) => {
   if (pickedProjectId == null || pickedProjectId === currentProjectId) return {};

   return { project_id: pickedProjectId, category_id: null, subcategory_id: null };
};

export const getTimeOptionsFromProject = (
   selectedItem: ProjectOptions | undefined,
   defaultTime: number,
   timeKey: BaggageTimeKey,
) => {
   if (selectedItem) {
      const timeId = selectedItem.baggage[timeKey] ?? defaultTime;
      const timeLabel = timeOptions.find(({ id }) => id === timeId)?.label;
      return { id: timeId, label: timeLabel };
   }
};

export const getTotalHours = (endTime: number, startTime: number): number => {
   const totalHours = endTime >= startTime ? endTime - startTime : 24 + endTime - startTime;
   return totalHours;
};

// Function to transform overtime rates to use numeric keys
export const transformOvertimeRates = (
   overtimeRates: OverTimeRates | { [key: string]: number },
) => {
   const transformedRates: { [key: number]: number } = {};

   for (const [dayName, rate] of Object.entries(overtimeRates)) {
      const dayNumber =
         dayNameToNumberMap[dayName.toLowerCase() as keyof typeof dayNameToNumberMap];
      // Check if dayNumber is not undefined (include 0)
      if (dayNumber !== undefined) {
         // If rate is null or undefined, set it to 0
         transformedRates[dayNumber] = rate ?? 0;
      }
   }

   return transformedRates;
};

// Function to reverse transform overtime rates to use day names
export const reverseTransformOvertimeRates = (transformedRates: { [key: number]: number }) => {
   const originalRates: { [key: string]: number } = {};

   for (const [dayNumber, rate] of Object.entries(transformedRates)) {
      const dayName = numberToDayNameMap[Number(dayNumber) as keyof typeof numberToDayNameMap];

      // Check if dayName is defined in the map
      if (dayName !== undefined) {
         // Assign to the original structure using day names
         originalRates[dayName] = rate ?? 0;
      }
   }

   return originalRates;
};

export const getFileDimensions = (file: string) => {
   return new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.src = file;
      img.addEventListener("load", () => resolve({ width: img.width, height: img.height }));
   });
};

export const resizeFile = (
   file: File,
   maxSize: number,
   minSize?: number,
   outputType: string = "file",
) => {
   return new Promise((resolve) => {
      Resizer.imageFileResizer(
         file,
         maxSize,
         maxSize,
         "png",
         100,
         0,
         (uri) => {
            resolve(uri);
         },
         outputType,
         minSize ?? undefined,
         minSize ?? undefined,
      );
   });
};

export const readFile = (file: File) => {
   return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => resolve(reader.result), false);
      reader.readAsDataURL(file);
   });
};

export const readFileFromUrl = async (name: string, url: string) => {
   const response = await fetch(url);
   const blob = await response.blob();
   return new File([blob], name ?? "file_name.png", {
      type: blob.type,
   });
};

export function prepareAttachmentsPayload(
   attachments: SerializedAttachment[],
   deletedAttachmentIds: string[],
) {
   const payload: AttachmentsData = {
      attachment_ids: getUniqueAttachmentIds(attachments, deletedAttachmentIds),
   };

   return payload;
}

export function getUniqueAttachmentIds(
   attachments: SerializedAttachment[],
   deletedAttachmentIds: string[],
) {
   // Get array of attachment IDs from attachments by user
   const addedAttachmentIds = attachments.map((attachment) => attachment.id);

   // addedAttachmentIds, and convert to Set for uniqueness to remove duplicates if any
   let uniqueAttachmentIds: any = new Set([...addedAttachmentIds]);

   // Remove IDs that are in deletedAttachmentIds array
   deletedAttachmentIds.forEach((id) => {
      uniqueAttachmentIds.delete(id);
   });

   // Convert Set back to array
   uniqueAttachmentIds = Array.from(uniqueAttachmentIds);

   return uniqueAttachmentIds;
}

export function prepareTagPayload(
   attachments: SerializedAttachment[],
   deletedAttachmentIds: string[],
   selectedTagId: string,
   expirationDate?: Date | null,
) {
   const payload: TagData = {
      tag_instances: {
         [selectedTagId]: {
            attachment_ids: getUniqueAttachmentIds(attachments, deletedAttachmentIds),
            expr_date: expirationDate ? getDetachedDay(expirationDate) : null,
         },
      },
   };

   return payload;
}
export const prepareActivitiesPayload = (
   entityId: string,
   entityType: string,
   includedCategories: string[] | string,
   limitActivities: number,
   nextStartingAfter?: string,
) => {
   const payload = {
      entity_id: entityId,
      entity_type: entityType as ActivityEntityType,
      included_categories: includedCategories as ActivityCategory[],
      limit: limitActivities,
      starting_after: nextStartingAfter,
   };
   return payload;
};

export function formatDate(date?: number, locale?: string): string {
   if (!date) return "";
   const isTimestamp = date.toString().length > 8;
   const dateFull = isTimestamp ? new Date(date) : getAttachedDate(date);
   const localDate = dateFull.toLocaleDateString(locale);
   return localDate;
}

export const formatCurrency = (
   value?: number,
   I18n?: ReturnType<typeof useI18nContext>,
): string => {
   if (!value) {
      return "";
   }
   const currencyOptions = { locale: I18n?.currentLocale() ?? "en", currencyIsoCode: "USD" };
   const currencyFormatter = new CurrencyFormatter(currencyOptions);
   return currencyFormatter.formatCurrency(value);
};

export const getTranslationLabel = (label: string, I18n: any): string => {
   const translationItem = translationsLabelsActivityCard.find((item) => item.name === label);
   return translationItem ? I18n.t(translationItem.translation) : label;
};

/**
 * Formats work days as translated day ranges, Sunday first: consecutive days collapse into a
 * range and separate groups are comma-separated, e.g. "Mo-Fr" or "Mo-We, Fr".
 */
export const formatWorkDays = (
   workDays: WorkDays,
   I18n: ReturnType<typeof useI18nContext>,
): string => {
   const dayLabel = (day: number) =>
      I18n.t(`views.company.workforce_planning.${daysOfWeekAbbreviations[day]}`);
   const groups: string[] = [];
   let groupStart: number | null = null;

   for (let day = 0; day <= daysOfWeekAbbreviations.length; day++) {
      if (workDays[day as keyof WorkDays]) {
         groupStart ??= day;
      } else if (groupStart != null) {
         const groupEnd = day - 1;
         groups.push(
            groupStart === groupEnd
               ? dayLabel(groupStart)
               : `${dayLabel(groupStart)}-${dayLabel(groupEnd)}`,
         );
         groupStart = null;
      }
   }

   return groups.join(", ");
};

export const customFieldsCard = (
   data: ActivitiesResponseProps,
   I18n: ReturnType<typeof useI18nContext>,
) => {
   const { custom_field_name, custom_field_type, new_val, old_val } = data.meta ?? {};

   if (!data.meta) return null;

   switch (custom_field_type) {
      case CustomFieldType.TEXT:
      case CustomFieldType.PARAGRAPH:
      case CustomFieldType.SELECT:
         if (new_val !== null && old_val !== null)
            return renderChange(custom_field_name, I18n, old_val, new_val);
         if (new_val === null && old_val !== undefined)
            return renderRemove(custom_field_name, old_val, I18n);
         if (old_val === null && new_val !== undefined)
            return renderAdd(custom_field_name, new_val, I18n);
         break;
      case CustomFieldType.MULTI_SELECT: {
         // Multi-select values arrive as arrays; legacy lists them comma-separated.
         const join = (value: unknown) =>
            (Array.isArray(value) ? value.join(", ") : value) as ValueType;
         if (new_val != null && old_val != null)
            return renderChange(custom_field_name, I18n, join(old_val), join(new_val));
         if (old_val != null) return renderRemove(custom_field_name, join(old_val), I18n);
         if (new_val != null) return renderAdd(custom_field_name, join(new_val), I18n);
         break;
      }
      case CustomFieldType.NUMBER: {
         const format = (value: unknown) =>
            formatNumber(value as number, {
               locale: I18n.currentLocale(),
               minimumFractionDigits: 0,
            });
         if (new_val != null && old_val != null)
            return renderChange(custom_field_name, I18n, format(old_val), format(new_val));
         if (old_val != null) return renderRemove(custom_field_name, format(old_val), I18n);
         if (new_val != null) return renderAdd(custom_field_name, format(new_val), I18n);
         break;
      }
      case CustomFieldType.DATE:
         if (new_val !== null && old_val !== null)
            return renderChange(custom_field_name, I18n, formatDate(old_val), formatDate(new_val));
         if (new_val === null && old_val !== null)
            return renderRemove(custom_field_name, formatDate(old_val), I18n);
         if (old_val === null && new_val !== null)
            return renderAdd(custom_field_name, formatDate(new_val), I18n);
         break;
      case CustomFieldType.BOOL: {
         const yesValue = I18n.t("views.company.workforce_planning.boolean_values.yes_value");
         const noValue = I18n.t("views.company.workforce_planning.boolean_values.no_value");
         if (new_val !== null && old_val !== null) {
            return renderChange(
               custom_field_name,
               I18n,
               old_val ? yesValue : noValue,
               new_val ? yesValue : noValue,
            );
         }
         if (new_val === null && old_val !== null) {
            return renderRemove(custom_field_name, old_val ? yesValue : noValue, I18n);
         }
         if (old_val === null && new_val !== null) {
            return renderAdd(custom_field_name, new_val ? yesValue : noValue, I18n);
         }
         break;
      }
      case CustomFieldType.CURRENCY:
         if (new_val !== null && old_val !== null)
            return renderChange(
               custom_field_name,
               I18n,
               formatCurrency(old_val, I18n),
               formatCurrency(new_val, I18n),
            );
         if (new_val === null && old_val !== null)
            return renderRemove(custom_field_name, formatCurrency(old_val, I18n), I18n);
         if (old_val === null && new_val !== null)
            return renderAdd(custom_field_name, formatCurrency(new_val, I18n), I18n);
         break;
      case CustomFieldType.HEX_COLOR:
         if (new_val !== null && old_val !== null)
            return renderColorChange(custom_field_name, I18n, old_val, new_val);
         if (new_val === null && old_val !== undefined)
            return renderColorRemove(custom_field_name, old_val, I18n);
         if (old_val === null && new_val !== undefined)
            return renderColorAdd(custom_field_name, new_val, I18n);
         break;
      default:
         return null;
   }
};

export const groupByCategories = (items: any[]) => {
   const groups: { [key: string]: any[] } = {};

   if (!items.length) return groups;

   items.forEach((item: any) => {
      const categories = item.categories.length > 0 ? item.categories : ["All Tags"];

      categories.forEach((category: string) => {
         if (!groups[category]) {
            groups[category] = [];
         }
         groups[category].push(item);
      });
   });
   return groups;
};

export function updateStartDateIfFuture(
   projectStartDate: Date | null,
   isCreateFlow: boolean,
   setInitialValues: Dispatch<SetStateAction<any>>,
   page?: PageTitle,
) {
   if (page === PageTitle.SCHEDULE_CARD) {
      setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
         ...prevValues,
         start_date: new Date(),
      }));
   }
   if (projectStartDate && isCreateFlow && isAfter(projectStartDate, new Date())) {
      setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
         ...prevValues,
         start_date: projectStartDate,
      }));
   }
}

export function updateProjectEndDate(
   projectEndDate: Date | null,
   isCreateFlow: boolean,
   setInitialValues: Dispatch<SetStateAction<any>>,
) {
   setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
      ...prevValues,
      end_date: null, // Reset end date to null initially
   }));
   if (projectEndDate && isCreateFlow) {
      const endDateToUse = getValidEndDate(projectEndDate);
      setInitialValues((prevValues: AssignmentFormValues | RequestFormValues) => ({
         ...prevValues,
         end_date: endDateToUse,
      }));
   }
}

// Determines the appropriate end date to use, defaulting to today if the project end date is in the past

export function getValidEndDate(estEndDate: Date | number | null | undefined): Date | null {
   if (!estEndDate) return null;

   // est_end_date arrives from the API as a UTC-midnight ms timestamp representing
   // a calendar day. Normalize to a local-midnight Date on that same calendar day
   // so downstream consumers (Form.DateSelect, formatDateTime with local TZ) don't
   // shift it by a day in negative-offset zones.
   const projectEndDate =
      typeof estEndDate === "number"
         ? convertUtcDateValueToDateWithoutTimezone(estEndDate)
         : estEndDate;
   if (!projectEndDate) return null;

   const today = new Date();
   today.setHours(0, 0, 0, 0);

   return isAfter(today, projectEndDate) ? today : projectEndDate;
}

export function isEndDateInPast(endDate: Date | number | null | undefined): boolean {
   if (!endDate) return false;
   const date =
      typeof endDate === "number" ? convertUtcDateValueToDateWithoutTimezone(endDate) : endDate;
   if (!date) return false;
   const today = new Date();
   today.setHours(0, 0, 0, 0);
   return isAfter(today, date);
}

export const deepEqual = (a: any, b: any): boolean => {
   if (a === b) return true;

   // Handle NaN comparison
   if (typeof a === "number" && typeof b === "number" && isNaN(a) && isNaN(b)) {
      return true;
   }

   // Ensure type consistency between arrays and objects
   if (Array.isArray(a) !== Array.isArray(b)) return false;

   // Handle Date comparison
   if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
   }

   // Type mismatch check
   if (typeof a !== typeof b) return false;

   // Handle object and array comparison
   if (a && b && typeof a === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      // Check if both objects/arrays have the same number of keys
      if (keysA.length !== keysB.length) return false;

      // Use a Set for faster lookup instead of includes
      const keysBSet = new Set(keysB);

      for (const key of keysA) {
         if (!keysBSet.has(key) || !deepEqual(a[key], b[key])) {
            return false;
         }
      }

      return true;
   }

   return false;
};

export const validateWorkDayTimeAndAllocationForBulkAssignmentAndRequest = (
   data: AssignmentListData | RequestListData,
   formStartDay: number | null,
   formEndDay: number | null,
   isFormByAllocation: boolean,
   isFormByTime: boolean,
   I18n: ReturnType<typeof useI18nContext>,
): string[] => {
   const reasons: string[] = [];

   // Workday validation
   if (formStartDay !== null) {
      const dayOfWeek = getAttachedDate(formStartDay).getDay();
      if (!data.work_days_object?.[dayOfWeek as keyof AssignmentWorkDays]) {
         reasons.push(
            I18n.t("views.company.workforce_planning.assignment.bulk.edit.validation.work_days", {
               field: I18n.t("views.company.workforce_planning.start_date"),
               work_days: data.work_days,
            }),
         );
      }
   }

   if (formEndDay !== null) {
      const dayOfWeek = getAttachedDate(formEndDay).getDay();
      if (!data.work_days_object?.[dayOfWeek as keyof AssignmentWorkDays]) {
         reasons.push(
            I18n.t("views.company.workforce_planning.assignment.bulk.edit.validation.work_days", {
               field: I18n.t("views.company.workforce_planning.end_date"),
               work_days: data.work_days,
            }),
         );
      }
   }

   // Time and allocation validation
   if (isFormByAllocation && (data.start_time || data.end_time)) {
      reasons.push(
         I18n.t(
            "views.company.workforce_planning.assignment.bulk.edit.validation.percent_allocation",
         ),
      );
   }

   if (isFormByTime && data.percent_allocated) {
      reasons.push(
         I18n.t(
            "views.company.workforce_planning.assignment.bulk.edit.validation.start_time_end_time",
         ),
      );
   }

   return reasons;
};

export function calculateTbdDate(startDate: Date | number, companyTbdWeeks: number): Date {
   // Normalize a UTC-midnight input to local-midnight on the same calendar day so
   // addWeeks/getDay return consistent results in any timezone. A Date passed in
   // is assumed to already be at local midnight on the intended calendar day.
   const normalizedStart =
      typeof startDate === "number"
         ? convertUtcDateValueToDateWithoutTimezone(startDate)
         : new Date(startDate);
   if (!normalizedStart) {
      throw new Error("calculateTbdDate: startDate could not be normalized");
   }

   const intermediateDate = addWeeks(normalizedStart, companyTbdWeeks);
   const dayOfWeek = intermediateDate.getDay();

   // If the result lands on Saturday or Sunday, walk back to the previous Friday.
   let adjustment = 0;
   if (dayOfWeek === 6 || dayOfWeek === 0) {
      adjustment = dayOfWeek === 6 ? 1 : 2;
   }

   const endDate = new Date(intermediateDate);
   endDate.setDate(intermediateDate.getDate() - adjustment);
   return endDate;
}
